import React, { useMemo } from 'react';
import {
  Alert, Checkbox, Col, InputNumber, Radio, Row, Space, Spin, Switch, Tag, Typography,
} from 'antd';
import {
  AUDIENCE_OPTIONS,
  CORPORATE_ROSTER_BASIS,
  ELIGIBILITY_STACK_NOTE,
  ELIGIBILITY_PRESETS,
  describeEligibilityMembers,
  normalizeEligibilityRaw,
  resolveAgeBounds,
  resolveGenderOptions,
  resolveMemberScopeOptions,
  validateEligibility,
} from './careProgramEligibility';

const { Text, Paragraph } = Typography;

const CareProgramEligibilityStep = ({
  eligibility = {},
  schema,
  schemaLoading = false,
  disabled = false,
  validationErrors = [],
  onChange,
}) => {
  const e = normalizeEligibilityRaw(eligibility);
  const memberScopeOptions = useMemo(() => resolveMemberScopeOptions(schema), [schema]);
  const genderOptions = useMemo(() => resolveGenderOptions(schema), [schema]);
  const ageBounds = useMemo(() => resolveAgeBounds(schema), [schema]);

  const schemaReady = memberScopeOptions.length > 0 && genderOptions.length > 0;
  const localErrors = useMemo(
    () => (schemaReady ? validateEligibility(e, schema) : []),
    [e, schema, schemaReady],
  );
  const allErrors = [...new Set([...validationErrors, ...localErrors])];

  const patch = (partial) => onChange?.({ ...e, ...partial });

  const applyPreset = (preset) => {
    onChange?.({
      ...e,
      ...preset.patch,
      genders: [...(preset.patch.genders ?? [])],
    });
  };

  const preview = describeEligibilityMembers(e, { memberScopeOptions, genderOptions });
  const isCorporate = e.audience !== 'all_enrollees';

  if (schemaLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin tip="Loading eligibility options from API…" />
      </div>
    );
  }

  if (!schemaReady) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Eligibility schema not loaded"
        description="Could not load member_scope_options and gender_options from GET /v2/admin/care-programs/schema. Eligibility cannot be configured until the API returns the catalog."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {allErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="Fix eligibility before saving"
          description={(
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {allErrors.map((err) => <li key={err}>{err}</li>)}
            </ul>
          )}
        />
      )}

      <Alert
        type="info"
        showIcon
        message="How the HR roster is built"
        description={(
          <div>
            {isCorporate ? (
              <>
                <div><Text strong>Starting point: </Text>{CORPORATE_ROSTER_BASIS}</div>
                <div style={{ marginTop: 6 }}>{ELIGIBILITY_STACK_NOTE}</div>
              </>
            ) : (
              <div>Audience is set to all enrollee types — roster rules follow the platform definition for non-corporate programmes.</div>
            )}
          </div>
        )}
      />

      <div>
        <Text strong>Program template</Text>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Optional quick-picks — you can still override every field below.
        </Paragraph>
        <Space wrap>
          {ELIGIBILITY_PRESETS.map((preset) => (
            <Tag
              key={preset.key}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', padding: '4px 10px' }}
              onClick={() => !disabled && applyPreset(preset)}
            >
              {preset.label}
            </Tag>
          ))}
        </Space>
      </div>

      <div>
        <Text strong>Policy audience</Text>
        <Radio.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
          disabled={disabled}
          value={e.audience}
          onChange={(ev) => patch({ audience: ev.target.value })}
        >
          {AUDIENCE_OPTIONS.map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              <Text>{opt.label}</Text>
              {opt.description ? (
                <div><Text type="secondary" style={{ fontSize: 12 }}>{opt.description}</Text></div>
              ) : null}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      <div>
        <Text strong>Member scope</Text>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Narrows the paid roster for this employer. Combined with gender and age (all must match). Facility check-in and HR export use the same rules.
        </Paragraph>
        <Radio.Group
          style={{ width: '100%' }}
          disabled={disabled}
          value={e.member_scope}
          onChange={(ev) => patch({ member_scope: ev.target.value })}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {memberScopeOptions.map((opt) => (
              <Radio key={opt.value} value={opt.value} style={{ alignItems: 'flex-start' }}>
                <Text strong>{opt.label}</Text>
                {opt.description ? (
                  <div><Text type="secondary" style={{ fontSize: 12 }}>{opt.description}</Text></div>
                ) : null}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
        {e.member_scope === 'all_members' && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Very broad scope"
            description="All members can include inactive enrollees. For employer staff medicals, Active principals only is usually correct — and remember inactive policies are never on the HR roster regardless of scope."
          />
        )}
      </div>

      <div>
        <Text strong>Gender</Text>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Leave none selected for any gender. Select one or both to restrict.
        </Paragraph>
        <Checkbox.Group
          disabled={disabled}
          value={e.genders}
          onChange={(vals) => patch({ genders: vals })}
          options={genderOptions.map((g) => ({ label: g.label, value: g.value }))}
        />
      </div>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Text strong>Minimum age (years)</Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
            Optional. Enrollees without date of birth are excluded when any age bound is set.
          </Paragraph>
          <InputNumber
            style={{ width: '100%' }}
            disabled={disabled}
            min={ageBounds.min}
            max={ageBounds.max}
            placeholder="No minimum"
            value={e.age_min}
            onChange={(v) => patch({ age_min: v ?? null })}
          />
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Maximum age (years)</Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
            Optional inclusive maximum (e.g. 17 for under-18 programs).
          </Paragraph>
          <InputNumber
            style={{ width: '100%' }}
            disabled={disabled}
            min={ageBounds.min}
            max={ageBounds.max}
            placeholder="No maximum"
            value={e.age_max}
            onChange={(v) => patch({ age_max: v ?? null })}
          />
        </Col>
      </Row>

      <div>
        <Space>
          <Switch
            disabled={disabled}
            checked={e.require_org_enabled !== false}
            onChange={(v) => patch({ require_org_enabled: v })}
          />
          <Text>Require organization to enable this program</Text>
        </Space>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}>
          When on, HR and facilities only see the program after admin enables it on the employer org.
        </Paragraph>
      </div>

      <Alert
        type="success"
        showIcon
        message="Eligible members preview"
        description={(
          <div>
            {isCorporate && (
              <div style={{ marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>On active org policies, narrowed to: </Text>
              </div>
            )}
            <Text>{preview}</Text>
          </div>
        )}
      />
    </Space>
  );
};

export default CareProgramEligibilityStep;
